import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useMediaQuery} from 'react-responsive';
import {useLocation} from 'react-router-dom';
import {compose} from 'redux';
import * as Yup from 'yup';
import {get} from '../../../../../api/api';
import moment from 'moment';
import {
  getAppointmentSpecialities,
  uriGetAppointmentDaysWithSlots,
  uriGetAppointmentsByTime,
  uriGetDoctorsSpeciality,
  uriGetSpecialties,
} from '../../../../../api/endpoints';
import AppButton from '../../../../../components/app-button';
import AppSpinner from '../../../../../components/app-spinner';
import {AppForm} from '../../../../../components/forms';
import Icon from '../../../../../components/icon';
import AppSelectInput from '../../../../../components/input/app-select-input';
import {Column, Row} from '../../../../../components/layout';
import {addToWizard, setSelectedBu} from '../../../../../store/actions';
import colors from '../../../../../util/colors';
import * as fonts from '../../../../../util/fonts';
import {Icons} from '../../../../../util/icons';
import {strings} from '../../../../../util/strings';
import {timeWithTimezoneToApi} from '../../../../../util/utility';
import AppointmentsList from '../../appointments-list/AppointmentsList';
import DoctorsResultList from '../../doctors-list-item/DoctorsResultList';
import DayTimeFilter from './components/day-time-filter/DayTimeFilter';
import DoctorFilter from './components/doctor-filter/DoctorFilter';
import './styles.scss';

interface SpecialitySelectProps {
  buStyles: any;
  selectedBu: any;
  labels: any;
  language: any;
  user: any;
  busMap: any;
  wizard: any;
  availableBus: any;
  setSelectedBu(value: any): void;
  nextStep(): void;
  onSubmit(values: any): any;
  addToWizard(value: any): void;
  renderClose(): void;
  stringsDefault: any;
}

const validationSchema = Yup.object().shape({});

function SpecialitySelect({
  buStyles,
  selectedBu,
  labels,
  language,
  wizard,
  user,
  stringsDefault,
  nextStep,
  addToWizard,
  renderClose,
}: SpecialitySelectProps) {
  const [valid, setValid] = useState(false);
  const [options, setOptions]: any[] = useState([]);
  const [selectedOption, setSelectedOption]: any = useState(
    wizard.state?.speciality || ''
  );
  const [otherDoctors, setOtherDoctors]: any[] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [showDoctors, setShowDoctors] = useState(false);
  const [doctorsEmpty, setDoctorsEmpty] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [showDayTime, setShowDayTime] = useState(
    wizard.state?.showDayTime || false
  );
  const [showDoctorSearch, setShowDoctorSearch] = useState(
    wizard.state?.showDoctorSearch || false
  );
  const [showNoDoctorsFound, setShowNoDoctorsFound] = useState(false);
  const [doctorsFound, setDoctorsFound] = useState([]);
  const [dayTimeSlots, setDayTimeSlots]: any[] = useState([]);
  const [doctorAvailableSlots, setDoctorAvailableSlots]: any[] = useState([]);
  const [
    doctorAvailableSlotsFromToday,
    setDoctorAvailableSlotsFromToday,
  ]: any[] = useState(null);

  const [showDayTimeSlots, setShowDayTimeSlots] = useState(false);
  const [enableMedicInfo, setEnableMedicInfo] = useState(false);
  const [showNoDayTimeSlots, setShowNoDayTimeSlots] = useState(false);
  const [appointmentTime, setAppointmentTime]: any = useState({});
  const [selectedDoctor, setSelectedDoctor] = useState(
    wizard.state?.selectedDoctor || null
  );
  const [searchValue, setSearchValue] = useState(
    wizard.state?.searchValue || null
  );
  const [initialDate, setInicialDate]: any = useState(
    wizard.state?.initialDate ? wizard.state.initialDate : undefined
  );
  const [firstDateAvailable, setFirstDateAvailable] = useState<any>(null);
  const location: any = useLocation();
  const editMode = !!location.state.editingAppointmentId;

  const isResponsive = useMediaQuery({maxDeviceWidth: 768});

  const formatSpecialities = (specialities: any) => {
    const specialityOptions = specialities?.map((el: any) => {
      return {
        label: el?.labelFrontend,
        alias: el?.specialtyAlias,
      };
    });
    setOptions(specialityOptions);
    const state = wizard.state
      ? {
          state: {
            ...wizard.state,
            options: specialityOptions,
          },
        }
      : {state: {options: specialityOptions}};
    addToWizard(state);
  };

  useEffect(() => {
    setSelectedOption('');
    formatSpecialities(wizard.patientInsurance.specialities);
  }, []);

  useEffect(() => {
    selectedDoctor && getDoctorAvailableSlotsFromToday(selectedDoctor);
  }, [selectedDoctor]);

  useEffect(() => {
    if (doctorAvailableSlotsFromToday?.[0]) {
      const firstDayAvailable = moment(
        doctorAvailableSlotsFromToday?.filter((date: any) => {
          return moment(date, 'DD-MM-YYYY').isSameOrAfter(moment(), 'day');
        })?.[0],
        'DD-MM-YYYY'
      ).format('YYYY-MM-DD');

      setFirstDateAvailable(firstDayAvailable);
      setInicialDate(firstDayAvailable);
    }
  }, [doctorAvailableSlotsFromToday]);

  useEffect(() => {
    setDoctorsFound([]);
    setShowNoDoctorsFound(false);
  }, [showDoctorSearch]);

  useEffect(() => {
    if (selectedDoctor) {
      setInicialDate(firstDateAvailable);
    }
    const state = {
      ...wizard.state,
      selectedDoctor: selectedDoctor,
      searchValue: searchValue,
    };
    addToWizard({state});
  }, [selectedDoctor]);

  useEffect(() => {
    if (
      options.length > 0 &&
      wizard.state?.speciality &&
      selectedOption !== ''
    ) {
      setSelectedOption(wizard.state.speciality);
      loadAppointmentList(wizard.state.speciality);
    }
  }, [options]);

  useEffect(() => {
    isLoading && setShowNoDoctorsFound(false);
  }, [isLoading]);

  const getAppointmentList = async (
    buId: any,
    memId: any,
    famId: any,
    specialityAlias: any
  ) => {
    try {
      setIsLoading(true);
      await get(
        uriGetDoctorsSpeciality(
          buId,
          memId,
          famId,
          specialityAlias.alias,
          editMode,
          specialityAlias.priceCalculation.procCodeRef,
          specialityAlias.priceCalculation.network,
          specialityAlias.priceCalculation.amtClaimed,
          specialityAlias.priceCalculation.providerId,
          specialityAlias.priceCalculation.practiceSeq,
          selectedBu.expDate
        )
      ).then((res: any) => {
        setOtherDoctors(res?.data?.items);
        res?.data?.items?.length < 1
          ? setDoctorsEmpty(true)
          : setDoctorsEmpty(false);
      });
      setShowDoctors(true);
    } catch (err: any) {
      setDoctorsEmpty(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getSpecialityAlias = (specialityLabel: any) => {
    const optionSelectedAlias = options.find(
      (el: any) => el.label === specialityLabel
    );
    const speciality = wizard.patientInsurance.specialities.find(
      (el: any) => optionSelectedAlias.alias == el.specialtyAlias
    );
    return {
      alias: speciality.integratorSpecialty,
      priceCalculation: speciality.priceCalculation,
    };
  };

  const getSpecialitySlug = (specialityLabel: any) => {
    const optionSelectedAlias = options.find(
      (el: any) => el.label === specialityLabel
    );
    const speciality = wizard.patientInsurance.specialities.find(
      (el: any) => optionSelectedAlias.alias == el.specialtyAlias
    );
    return speciality.specialtyAlias;
  };

  const loadAppointmentList = async (optionSelected: any) => {
    const buId = wizard.patientInsurance?.patient.buId || 'NOTCLIENT';
    const memId = wizard.patientInsurance?.patient.memId || user.nif;
    const famId = wizard.patientInsurance?.patient.famId || user.nif;
    const specialityAlias = getSpecialityAlias(optionSelected);
    getAppointmentList(buId, memId, famId, specialityAlias);
  };

  const getSlotsByTime = (
    startTime: any,
    startDate: any,
    endTime: any,
    endDate: any,
    specialityAlias: any,
    doctorId?: any[]
  ) => {
    setAppointmentTime({});
    setIsLoading(true);
    const optionSelectedAlias = wizard.state.speciality;

    const speciality = wizard.patientInsurance.specialities.find(
      (el: any) => optionSelectedAlias === el.labelFrontend
    );
    const buId = wizard.patientInsurance?.patient.buId || 'NOTCLIENT';
    const memId = wizard.patientInsurance?.patient.memId || user.nif;
    const famId = wizard.patientInsurance?.patient.famId || user.nif;
    get(
      uriGetAppointmentsByTime(
        buId,
        memId,
        famId,
        speciality.integratorSpecialty,
        startDate,
        doctorId ? startTime : timeWithTimezoneToApi(startTime, startDate),
        endDate,
        doctorId ? endTime : timeWithTimezoneToApi(endTime, startDate),
        editMode,
        specialityAlias.priceCalculation.procCodeRef,
        specialityAlias.priceCalculation.network,
        specialityAlias.priceCalculation.amtClaimed,
        specialityAlias.priceCalculation.providerId,
        specialityAlias.priceCalculation.practiceSeq,
        selectedBu.expDate,
        doctorId
      )
    )
      .then((res: any) => {
        setDayTimeSlots(res?.data?.items);
        if (!res?.data?.items || res?.data?.items?.length < 1) {
          !showDoctorSearch && setShowNoDayTimeSlots(true);
          showDoctorSearch && setShowNoDoctorsFound(true);
        } else {
          setShowNoDayTimeSlots(false);
        }
        setShowDayTimeSlots(true);
        setEnableMedicInfo(doctorId ? true : false);
      })
      .catch((err) => {
        if (err?.status === 404) {
          setDayTimeSlots([]);
          !showDoctorSearch && setShowNoDayTimeSlots(true);
          showDoctorSearch && setShowNoDoctorsFound(true);
          setShowDayTimeSlots(true);
          setEnableMedicInfo(doctorId ? true : false);
        } else {
          showDoctorSearch && setShowNoDoctorsFound(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getDoctorAvailableSlotsFromToday = (doctor: any) => {
    get(
      uriGetAppointmentDaysWithSlots(
        doctor.id, //doctor.id,
        moment().format('YYYY-MM-DD'),
        moment().add(12, 'months').endOf('month').format('YYYY-MM-DD')
      )
    )
      .then((res: any) => {
        const availableSlots = convertDates(res.data.collection);

        const momentDates = availableSlots.map((date: string) =>
          moment(date, 'DD-MM-YYYY')
        );
        const sortedMomentDates = momentDates.sort((a: any, b: any) => a - b);
        const sortedDates = sortedMomentDates.map((date: any) =>
          date.format('DD-MM-YYYY')
        );

        if (!res || res.data.collection.length < 1) {
          setDoctorAvailableSlotsFromToday([]);
          setInicialDate(new Date());

          const state = {
            ...wizard.state,
            initialDate: null,
          };
          addToWizard({state});
        } else {
          setDoctorAvailableSlotsFromToday(
            sortedDates?.filter((date: any) => {
              return moment(date, 'DD-MM-YYYY').isSameOrAfter(moment(), 'day');
            })
          );
        }
      })
      .catch((err) => {})
      .finally(() => {});
  };

  const getAvailableSlotsByTime = (
    startTime: any,
    startDate: any,
    endTime: any,
    endDate: any,
    doctor: any
  ) => {
    setIsLoadingAvailability(true);
    get(
      uriGetAppointmentDaysWithSlots(
        doctor.id, //doctor.id,
        startDate,
        endDate
      )
    )
      .then((res: any) => {
        const availableSlots = convertDates(res.data.collection);
        if (!res || res.data.collection.length < 1) {
          setDoctorAvailableSlots([]);
          !showDoctorSearch && setShowNoDayTimeSlots(true);
          showDoctorSearch && setShowNoDoctorsFound(true);
        } else {
          setDoctorAvailableSlots(availableSlots);
          setShowNoDayTimeSlots(false);
        }
      })
      .catch((err) => {
        showDoctorSearch && setShowNoDoctorsFound(true);
      })
      .finally(() => {
        setIsLoadingAvailability(false);
        setIsLoading(false);
        setShowDayTime(true);
      });
  };

  const convertDates = (slots: any[]) => {
    if (slots.length === 0) {
      return [];
    }
    const array = slots[0].availabilities;
    return array.map((slot: any) => {
      const [day, month, year] = slot?.split('-').map(Number);
      return new Date(year, month - 1, day);
    });
  };

  const handleSpecialityChange = (value: any) => {
    setSelectedOption(value);
    loadAppointmentList(value);

    const state = {
      ...wizard.state,
      speciality: value,
      specialitySlug: getSpecialitySlug(value),
    };
    addToWizard({state});
  };

  const handleDayTimeContinue = () => {
    const state = {
      ...wizard.state,
      showDayTime: showDayTime,
      initialDate: appointmentTime.appointmentSlots[0].startTime,
    };
    addToWizard({state});
    addToWizard({appointmentSlot: appointmentTime});
    setInicialDate(appointmentTime.appointmentSlots[0].startTime);
    nextStep && nextStep();
  };

  const handleGoBack = () => {
    setShowDayTime(false);
    setShowDoctorSearch(wizard.state?.goBackToSearchDoctor);
    setSelectedDoctor(null);
    setInicialDate(new Date());
    const state = {
      ...wizard.state,
      selectedDoctor: null,
      showDayTime: false,
      showDoctorSearch: wizard.state?.goBackToSearchDoctor,
    };
    addToWizard({state});
  };

  const handleOnDoctorClick = (doctor: any) => {
    setSelectedDoctor(doctor);
    setIsLoading(true);
    const monthStart = moment().clone().startOf('month').format('YYYY-MM-DD');
    const monthEnd = moment()
      .clone()
      .add(2, 'months')
      .startOf('month')
      .subtract(1, 'day')
      .endOf('month')
      .format('YYYY-MM-DD');

    getAvailableSlotsByTime('00:00', monthStart, '23:59', monthEnd, doctor);
  };

  const showAppointmentSlotsList =
    selectedOption && showDoctors && !isLoading && !doctorsEmpty;

  return (
    <>
      <Row className="patient-select-title-row">
        <p className="patient-select-title" style={{...fonts.ML}}>
          {showDayTime
            ? labels?.pt?.appointments?.appointments_schedule_day_time_title
            : showDoctorSearch
            ? labels?.pt?.appointments?.appointments_schedule_step2_title_doctor
            : labels?.pt?.appointments?.appointments_schedulo_step2_title}
        </p>
      </Row>
      <AppForm
        initialValues={{cardNumber: ''}}
        validationSchema={validationSchema}
        onSubmit={() => {}}
        onValidation={(valid) => setValid(valid)}>
        {!showDayTime && !showDoctorSearch && (
          <Column className={'speciality-select-box-column'}>
            <Row className="speciality-select-dropdown-row">
              <AppSelectInput
                className="speciality-input"
                options={options}
                value={selectedOption}
                defaultValue={''}
                onChange={(value: any) => {
                  handleSpecialityChange(value);
                }}
                customIcon={'DropdownInsuranceIcon'}
              />
            </Row>
          </Column>
        )}
        {showDoctorSearch && (
          <Column className={'doctor-search-box-column'}>
            <DoctorFilter
              goBack={() => handleGoBack()}
              doctorsFound={doctorsFound}
              showDayTime={showDayTime}
              setDoctorsFound={setDoctorsFound}
              setShowNoDoctorsFound={setShowNoDoctorsFound}
              setIsLoading={setIsLoading}
              setShowDayTime={setShowDayTime}
              selectedDoctor={selectedDoctor}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              speciality={getSpecialityAlias(wizard.state.speciality)}
            />
          </Column>
        )}
        {showDayTime && (
          <Column
            className={
              showDoctorSearch
                ? 'doctor-daytime-speciality-select-box-column'
                : 'daytime-speciality-select-box-column'
            }>
            <DayTimeFilter
              selectedDoctor={selectedDoctor}
              showDoctorSearch={showDoctorSearch}
              goBack={() => handleGoBack()}
              initialValue={initialDate}
              doctorAvailableSlots={doctorAvailableSlots}
              doctorAvailableSlotsFromToday={doctorAvailableSlotsFromToday}
              getSlotsByTime={(
                startTime: any,
                startDate: any,
                endTime: any,
                endDate: any,
                //specialty: any,
                doctor?: any
              ) => {
                getSlotsByTime(
                  startTime,
                  startDate,
                  endTime,
                  endDate,
                  getSpecialityAlias(wizard.state.speciality),
                  doctor
                );
              }}
              getAvailableSlotsByTime={(
                startTime: any,
                startDate: any,
                endTime: any,
                endDate: any
              ) => {
                getAvailableSlotsByTime(
                  startTime,
                  startDate,
                  endTime,
                  endDate,
                  selectedDoctor
                );
              }}
              isLoading={isLoadingAvailability}
              firstDateAvailable={firstDateAvailable}
            />
          </Column>
        )}
        {showAppointmentSlotsList && !showDayTime && !showDoctorSearch && (
          <>
            <Row className="speciality-doctors-list-title-row">
              <p
                style={{...fonts.MM}}
                className="speciality-doctors-list-title">
                {
                  labels?.pt?.appointments
                    ?.appointments_speciality_doctor_list_title
                }
              </p>
            </Row>
            <Row className="speciality-doctors-list">
              <AppointmentsList
                nextStep={nextStep}
                appointmentsItems={otherDoctors}
                borderColor={buStyles.primaryColor}
                setRefresh={setRefresh}
                isDoctors={true}
              />
            </Row>
          </>
        )}
        {doctorsEmpty && !isLoading && !showDayTime && !showDoctorSearch && (
          <Column className="no-doctors-column">
            <Icon
              className="reimbursement-no-results-history-filter-icon"
              name={Icons.ALERT_PROBLEM}
              color={colors.lightGrey}
              width="40"
              height="40"
              changeViewbox
              viewBox={'0 0 24 24'}
            />
            <h6
              className="reimbursement-list-header"
              style={{color: colors.lightGrey}}>
              {
                labels?.pt?.appointments
                  ?.appointments_speciality_doctor_list_no_results
              }
            </h6>
          </Column>
        )}
        {showNoDayTimeSlots && !isLoading && showDayTime && !showDoctorSearch && (
          <Column className="no-doctors-column">
            <Icon
              className="reimbursement-no-results-history-filter-icon"
              name={Icons.ALERT_PROBLEM}
              color={colors.lightGrey}
              width="40"
              height="40"
              changeViewbox
              viewBox={'0 0 24 24'}
            />
            <h6
              className="reimbursement-list-header"
              style={{color: colors.lightGrey}}>
              {
                labels?.pt?.appointments
                  ?.appointments_schedule_day_time_no_slots
              }
            </h6>
          </Column>
        )}
        {showNoDoctorsFound && !isLoading && showDoctorSearch && (
          <Column
            className="no-doctors-column no-doctors-column-responsive"
            style={{
              marginTop: showDayTime && isResponsive ? '14%' : 'inherit',
            }}>
            <Icon
              className="reimbursement-no-results-history-filter-icon"
              name={Icons.ALERT_PROBLEM}
              color={colors.lightGrey}
              width="40"
              height="40"
              changeViewbox
              viewBox={'0 0 24 24'}
            />
            <h6
              className="reimbursement-list-header no-doctor-found-error-title"
              style={{...fonts.MM, ...fonts.Regular, color: colors.lightGrey}}>
              {
                labels?.pt?.appointments
                  ?.appointments_scheduleByDoctorSearchNoResults
              }
            </h6>
            <h6
              className="reimbursement-list-header no-doctor-found-error-title"
              style={{...fonts.MM, ...fonts.Regular, color: colors.lightGrey}}>
              {
                labels?.pt?.appointments
                  ?.appointments_scheduleByDoctorSearchNoResults2
              }
              <span
                style={{color: buStyles.primaryColor, cursor: 'pointer'}}
                onClick={() => {
                  setShowDoctorSearch(false);
                  setShowDayTime(true);
                  setSelectedDoctor(null);
                  setInicialDate(new Date());
                  const state = {
                    ...wizard.state,
                    selectedDoctor: null,
                    goBackToSearchDoctor: true,
                  };
                  addToWizard({state});
                }}>
                {
                  labels?.pt?.appointments
                    ?.appointments_scheduleByDoctorSearchNoResultsDayAndHour
                }
              </span>
            </h6>
          </Column>
        )}

        {!showNoDoctorsFound &&
          !isLoading &&
          showDoctorSearch &&
          !showDayTime && (
            <DoctorsResultList
              doctorsFound={doctorsFound}
              setDoctorsFound={setDoctorsFound}
              setShowNoDoctorsFound={setShowNoDoctorsFound}
              setIsLoading={setIsLoading}
              setShowDayTime={setShowDayTime}
              setSelectedDoctor={setSelectedDoctor}
              handleOnDoctorClick={handleOnDoctorClick}
            />
          )}

        {showDayTimeSlots && showDayTime && !isLoading && !showNoDayTimeSlots && (
          <>
            {!showDoctorSearch && (
              <Row className="speciality-doctors-list-title-row">
                <p
                  style={{...fonts.MM}}
                  className="speciality-doctors-list-title">
                  {
                    labels?.pt?.appointments
                      ?.appointments_speciality_doctor_list_title
                  }
                </p>
              </Row>
            )}
            <Row className="speciality-doctors-list">
              <AppointmentsList
                nextStep={nextStep}
                appointmentsItems={dayTimeSlots}
                borderColor={buStyles.primaryColor}
                setRefresh={setRefresh}
                isDoctorsDayTime={true}
                setSelectedAppointmentTime={(value: any) =>
                  setAppointmentTime(value)
                }
                enableMedicInfo={enableMedicInfo}
              />
            </Row>
            <Row className="day-time-continue-row">
              <AppButton
                variant="primary"
                className="continue-button-day-time"
                disabled={appointmentTime === '' || !appointmentTime.id}
                onClick={() => {
                  handleDayTimeContinue();
                }}>
                {labels?.pt.login?.login_forgotPassword_continuar}
              </AppButton>
            </Row>
          </>
        )}
        {!isLoading && !showDayTime && !showDoctorSearch && selectedOption && (
          <>
            <>
              <Row className="other-views-row" style={{...fonts.MM}}>
                {labels?.pt?.appointments?.appointments_see_other_views}
              </Row>
              <Row className="view-buttons-row">
                {labels?.pt?.appointments
                  ?.appointments_findAppointmentByDoctorActive === '1' && (
                  <AppButton
                    className="day-time-button"
                    variant="secondary"
                    onClick={() => {
                      const state = {
                        ...wizard.state,
                        showDoctorSearch: true,
                        searchValue: null,
                      };
                      addToWizard({state});
                      setShowDoctorSearch(true);
                      setSearchValue(null);
                    }}>
                    {labels?.pt?.appointments?.appointments_by_medic_button}
                  </AppButton>
                )}
                <AppButton
                  className="day-time-button"
                  variant="secondary"
                  onClick={() => {
                    const state = {
                      ...wizard.state,
                      selectedDoctor: null,
                      searchValue: null,
                      setShowDayTime: true,
                    };
                    addToWizard({state});
                    setShowDayTime(true);
                  }}>
                  {labels?.pt?.appointments?.appointments_day_time_button}
                </AppButton>
              </Row>
            </>
            {!editMode && (
              <Row className="price-label-row" style={{...fonts.M}}>
                {labels?.pt?.appointments?.appointments_prices_may_vary}
              </Row>
            )}
          </>
        )}

        {isLoading && (
          <AppSpinner
            className={
              showDoctorSearch &&
              isResponsive &&
              !showDayTime &&
              'doctor-loading-icon'
            }
          />
        )}

        {isResponsive && (
          <Row className="exit-button-responsive">{renderClose()}</Row>
        )}
      </AppForm>
    </>
  );
}

const mapStateToProps = ({
  selectedBu,
  user,
  strings,
  globalStyles,
  busMap,
  availableBus,
  wizard,
  stringsDefault,
  language,
}: any) => ({
  user: user.value,
  selectedBu: selectedBu.value,
  language: language.value,
  buStyles: globalStyles.value,
  labels: strings.value,
  stringsDefault: stringsDefault.value,
  busMap: busMap.value,
  availableBus: availableBus.value,
  wizard: wizard.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  setSelectedBu: (value: any) => dispatch(setSelectedBu(value)),
  addToWizard: (value: any) => dispatch(addToWizard(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(SpecialitySelect)
);
